/**
 * Who is playing right now, and which season it is.
 *
 * Both of these read endpoints that were **implemented, deployed and consumed by nothing**:
 *
 *   - `GET /api/match/servers` — the arena room registry (`server-registry.service.ts`), which
 *     returns every live room's map, mode, phase and player count. The realtime tier has been
 *     heart-beating into it since P4 and no client had ever read it.
 *   - `GET /api/seasons/active` — the active season with its number, name, end date and the
 *     **server's own tier name** for this player (`season.service.ts`'s `getUserSeasonInfo`).
 *
 * That second one also settles a duplication this shell introduced: `play-panels.ts` derived a
 * tier name from hardcoded rank-point thresholds because "nothing exposes a tier name over the
 * API". Something does. The client-side ladder is gone and the server's answer is used, which
 * is the only version that cannot disagree with the rewards the season actually pays out.
 *
 * Nothing here fabricates. An empty registry says the lobby is quiet rather than inventing a
 * population, and a backend with no season configured renders no season panel at all — the
 * seasons table is genuinely allowed to be empty, and a menu that invented "Season 1" would be
 * lying about a thing players plan around.
 */
import { api } from '../../../core/network/api';

export interface ArenaRoomEntry {
  roomId: string;
  endpoint: string;
  mapName: string;
  mode: string;
  players: number;
  maxPlayers: number;
  phase: string;
  seenAt?: number;
}

export interface SeasonInfo {
  season: {
    id: string;
    name: string;
    number: number;
    startsAt: string;
    endsAt: string;
    status: string;
  };
  userRankPoints: number;
  userTier: string | null;
}

/**
 * Live rooms, or an empty list.
 *
 * Never throws. Every caller draws menu furniture, and a registry that is down must cost the
 * player a player-count, not their front door — the same contract the dossier and the daily
 * streak already hold themselves to.
 */
export async function fetchLiveRooms(): Promise<ArenaRoomEntry[]> {
  try {
    const rooms = await api.get<ArenaRoomEntry[]>('/match/servers');
    return Array.isArray(rooms) ? rooms : [];
  } catch {
    return [];
  }
}

/** The active season, or null when there is none configured or the call fails. */
export async function fetchSeason(): Promise<SeasonInfo | null> {
  try {
    return await api.get<SeasonInfo | null>('/seasons/active');
  } catch {
    return null;
  }
}

/**
 * Players currently in rooms of each mode key, and the total across all of them.
 *
 * Keyed by the room's `mode` string, which is the same key `PLAY_MODES.ticketMode` uses — the
 * registry writes what the room was created as, so the two agree by construction rather than by
 * a mapping table this file would have to keep in step.
 */
export function populationByMode(rooms: ArenaRoomEntry[]): { total: number; byMode: Map<string, number> } {
  const byMode = new Map<string, number>();
  let total = 0;
  for (const room of rooms) {
    // A room whose heartbeat is older than a minute is not evidence of anybody playing. The
    // registry expires its own keys, but a stale-but-unexpired entry would still inflate a
    // count the player is deciding on — better to under-report a live lobby than to send
    // someone into an empty one.
    if (room.seenAt !== undefined && Date.now() - room.seenAt > 60_000) continue;
    const players = Math.max(0, room.players | 0);
    total += players;
    byMode.set(room.mode, (byMode.get(room.mode) ?? 0) + players);
  }
  return { total, byMode };
}

/** Whole days left in the season, floored, or null when it has already ended. */
export function daysRemaining(season: SeasonInfo, now = new Date()): number | null {
  const ends = new Date(season.season.endsAt).getTime();
  if (!Number.isFinite(ends)) return null;
  const ms = ends - now.getTime();
  if (ms <= 0) return null;
  return Math.floor(ms / 86_400_000);
}
